'use client';

import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { useRouter } from 'next/navigation';
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Database } from '@/types/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { useProfile, useSignIn, useSignUp, useSignOut } from '@/hooks/useAuthApi';

// Define user role type based on Supabase enum
type UserRole = 'admin' | 'vendor' | 'customer';

// Define vendor information type
type VendorInfo = {
  id: string;
  company_name: string;
  status: string;
} | null;

// Define team member information type
type TeamMemberInfo = {
  role: 'admin' | 'manager' | 'staff';
  vendorId: string;
  permissions: Record<string, boolean>;
  isOwner: boolean;
  isTeamMember: boolean;
} | null;

// Define the enhanced User type with role and vendor info
type User = {
  id: string;
  email?: string;
  role?: UserRole;
  firstName?: string;
  lastName?: string;
  vendorId?: string;
  vendorInfo?: VendorInfo;
  teamInfo?: TeamMemberInfo;
} | null;

// Define the AuthContext type
type AuthContextType = {
  user: User;
  signUp: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  isAdmin: boolean;
  isVendor: boolean;
  isCustomer: boolean;
  isAuthReady: boolean;
  refreshProfile: () => void;
  // Team-related properties
  isTeamMember: boolean;
  isTeamAdmin: boolean;
  teamRole: 'admin' | 'manager' | 'staff' | null;
};

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  isLoading: true,
  isAdmin: false,
  isVendor: false,
  isCustomer: false,
  isAuthReady: false,
  refreshProfile: () => {},
  isTeamMember: false,
  isTeamAdmin: false,
  teamRole: null,
});

// AuthProvider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [, setSession] = useState<Session | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const router = useRouter();
  const supabase = createClientComponentClient<Database>();
  
  // Use API hooks for data fetching - only when user is authenticated
  const { 
    data: profileData, 
    isLoading: profileLoading, 
    refetch: refetchProfile
  } = useProfile({ enabled: !!supabaseUser });
  
  const signInMutation = useSignIn();
  const signUpMutation = useSignUp();
  const signOutMutation = useSignOut();

  // Transform profile data to legacy user format
  const user: User = supabaseUser ? {
    id: supabaseUser.id,
    email: supabaseUser.email,
    role: profileData?.profile?.role || 'customer',
    firstName: profileData?.profile?.first_name || undefined,
    lastName: profileData?.profile?.last_name || undefined,
    vendorId: profileData?.profile?.vendor_id || undefined,
    vendorInfo: profileData?.vendor ? {
      id: profileData.vendor.id,
      company_name: profileData.vendor.company_name,
      status: profileData.vendor.status
    } : null
  } : null;

  // Derived state
  const isAdmin = user?.role === 'admin';
  const isVendor = user?.role === 'vendor';
  const isCustomer = user?.role === 'customer';
  
  // Team-related derived state (these will be computed on-demand by components)
  const isTeamMember = Boolean(user?.teamInfo?.isTeamMember);
  const isTeamAdmin = Boolean(user?.teamInfo?.role === 'admin' || user?.teamInfo?.isOwner);
  const teamRole = user?.teamInfo?.role || null;

  // Handle auth state changes
  const handleAuthStateChange = useCallback(async (event: string, session: Session | null) => {
    
    setSession(session);
    setSupabaseUser(session?.user || null);
    
    if (session?.user) {
      // Profile will be fetched automatically by React Query
      // when supabaseUser is set
      
      // Only navigate to dashboard after actual sign in, not session refreshes
      // Avoid redirecting if user is already on an authenticated page
      if (event === 'SIGNED_IN') {
        const currentPath = window.location.pathname;
        const isOnPublicPage = ['/', '/login', '/signup'].includes(currentPath);
        
        // Only redirect if user is on a public/auth page, not when switching tabs on authenticated pages
        if (isOnPublicPage) {
          router.push('/dashboard');
        }
      }
    } else {
      // User signed out - clear state immediately
      setSupabaseUser(null);
      setSession(null);
      if (event === 'SIGNED_OUT') {
        router.push('/login');
      }
    }
    
    setIsLoading(false);
    setIsAuthReady(true);
  }, [router]);

  // Initialize auth
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (mounted) {
          setSession(initialSession);
          setSupabaseUser(initialSession?.user || null);
          setIsLoading(false);
          setIsAuthReady(true);
        }
      } catch (error) {
        if (mounted) {
          setIsLoading(false);
          setIsAuthReady(true);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase.auth, handleAuthStateChange]);

  // Auth action handlers
  const handleSignUp = async (email: string, password: string, captchaToken?: string) => {
    try {
      await signUpMutation.mutateAsync({ email, password, captchaToken });
      // User will be redirected to email confirmation page
    } catch (error) {
      throw error; // Let the component handle the error
    }
  };

  const ensureUserSetup = async () => {
    try {
      
      // Add a small delay to ensure session cookies are fully propagated
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await fetch('/api/auth/ensure-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        
        // Refresh profile to get updated user data
        await refreshProfile();
        
        // Return the result data for navigation decisions
        return result.data;
      } else {
        
        // If it's a 401, the session might not be fully propagated yet
        // Try one more time after a longer delay
        if (response.status === 401) {
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const retryResponse = await fetch('/api/auth/ensure-setup', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (retryResponse.ok) {
            const retryResult = await retryResponse.json();
            await refreshProfile();
            return retryResult.data;
          }
        }
        
        return null;
      }
    } catch (error) {
      // Don't fail login if setup check fails
      return null;
    }
  };

  const handleSignIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const result = await signInMutation.mutateAsync({ email, password, captchaToken });
      
      // Check if the response indicates verification is required
      if (result && 'requiresVerification' in result && result.requiresVerification) {
        // Redirect to verification page with email parameter
        const verificationUrl = `/verify-email?email=${encodeURIComponent(email)}`;
        router.push(verificationUrl);
        return;
      }
      
      // Small delay to ensure cookies are set properly
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After successful API login, refresh the client-side session
      // This ensures the client-side Supabase client picks up the new session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Manually trigger auth state change since onAuthStateChange might not fire
        setSession(session);
        setSupabaseUser(session.user);
        setIsLoading(false);
        setIsAuthReady(true);
        
        // Ensure user setup is complete before navigation
        const setupResult = await ensureUserSetup();
        
        // Navigate to appropriate page (only redirect from public pages)
        const currentPath = window.location.pathname;
        const isOnPublicPage = ['/', '/login', '/signup'].includes(currentPath);
        
        if (isOnPublicPage) {
          // If setup was completed and onboarding is not done, go to onboarding
          if (setupResult?.setup_completed && !setupResult?.onboarding_completed) {
            router.push('/onboarding');
          } else if (!setupResult?.has_subscription) {
            // Redirect to subscription payment if no valid subscription
            router.push('/subscription-payment');
          } else {
            router.push('/dashboard');
          }
        }
      } else {

        // Retry once more with longer delay
        await new Promise(resolve => setTimeout(resolve, 300));
        const { data: { session: retrySession } } = await supabase.auth.getSession();

        if (retrySession) {
          setSession(retrySession);
          setSupabaseUser(retrySession.user);
          setIsLoading(false);
          setIsAuthReady(true);

          // Ensure user setup is complete before navigation
          const setupResult = await ensureUserSetup();

          // Navigate to appropriate page (only redirect from public pages)
          const currentPath = window.location.pathname;
          const isOnPublicPage = ['/', '/login', '/signup'].includes(currentPath);

          if (isOnPublicPage) {
            // If setup was completed and onboarding is not done, go to onboarding
            if (setupResult?.setup_completed && !setupResult?.onboarding_completed) {
              router.push('/onboarding');
            } else if (!setupResult?.has_subscription) {
              // Redirect to subscription payment if no valid subscription
              router.push('/subscription-payment');
            } else {
              router.push('/dashboard');
            }
          }
        } else {
          throw new Error('Authentication failed - no session found');
        }
      }
    } catch (error) {
      throw error; // Let the component handle the error
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear user state immediately to stop API calls
      setSupabaseUser(null);
      setSession(null);
      setIsLoading(false);
      
      await signOutMutation.mutateAsync();
      // Force navigation immediately after logout API call
      // This ensures redirect happens even if auth state change is delayed
      router.push('/login');
    } catch (error) {
      // Ensure user state is cleared even if API call fails
      setSupabaseUser(null);
      setSession(null);
      setIsLoading(false);
      // Force navigation even if API call fails
      router.push('/login');
    }
  };

  const refreshProfile = () => {
    refetchProfile();
  };

  // Overall loading state
  const overallLoading = isLoading || (supabaseUser ? profileLoading : false);

  const value: AuthContextType = {
    user,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    isLoading: overallLoading,
    isAdmin,
    isVendor,
    isCustomer,
    isAuthReady,
    refreshProfile,
    isTeamMember,
    isTeamAdmin,
    teamRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};